import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Handshake } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STATUS = { offered: "Awaiting enhancer", active: "Active", declined: "Declined", withdrawn: "Withdrawn", ended: "Ended" };
const Pill = ({ s }) => <span className={`text-xs rounded-full px-2 py-0.5 ${s === "active" ? "bg-[#5C8A72] text-white" : s === "offered" ? "bg-[#D98E73]/20 text-[#b8694f]" : "bg-[#E5E0D8] text-[#4A5D6B]"}`}>{STATUS[s] || s}</span>;
const Split = ({ r }) => <span className="text-xs text-[#8FA89B]">EYW 10% · enhancer {r}% · center {Math.round((90 - r) * 100) / 100}% of each sale</span>;
const useRun = (keys) => {
  const qc = useQueryClient();
  return async (fn, ok) => { try { await fn(); toast.success(ok); keys.forEach((k) => qc.invalidateQueries({ queryKey: k })); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
};

const Row = ({ a, children, who }) => (
  <li className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`referral-agreement-${a.id}`}>
    <span className="flex-1 min-w-[180px]"><b className="text-[#1A242B]">{who}</b> · {a.rate_pct}% referral <Pill s={a.status} /><br /><Split r={a.rate_pct} />{a.note && <span className="block text-xs text-[#4A5D6B] italic">“{a.note}”</span>}</span>
    {children}
  </li>
);

const CenterDeliveredToggles = ({ services, run }) => (
  <div className="mt-6" data-testid="center-delivered-services">
    <p className="eyw-label mb-2">Services your center delivers itself</p>
    <p className="text-xs text-[#8FA89B] mb-3">Ticked services (float, sauna, footbath…) pay your center 90% of the sale, minus any referral rate agreed with the enhancer who referred the client.</p>
    {services.length === 0 ? <p className="text-sm text-[#8FA89B]">No center services yet.</p> : (
      <div className="grid sm:grid-cols-2 gap-1">
        {services.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-sm text-[#4A5D6B] px-2 py-1.5 rounded-lg hover:bg-[#144F75]/[0.04]" data-testid={`center-delivered-${s.id}`}>
            <input type="checkbox" checked={s.center_delivered} className="accent-[#144F75]"
              onChange={(e) => run(() => api.patch(`/manager/services/${s.id}/center-delivered`, { center_delivered: e.target.checked }), "Saved.")} />
            {s.title}
          </label>
        ))}
      </div>
    )}
  </div>
);

export const ManagerReferralPanel = () => {
  const run = useRun([["mgr-referrals"]]);
  const [f, setF] = useState({ enhancer_id: "", rate_pct: "", note: "" });
  const { data } = useQuery({ queryKey: ["mgr-referrals"], queryFn: () => api.get("/manager/referral-agreements").then((r) => r.data) });
  const { data: roster = [] } = useQuery({ queryKey: ["mgr-enhancers"], queryFn: () => api.get("/manager/me/enhancers").then((r) => r.data) });
  const approved = roster.filter((r) => r.status === "approved");
  const offer = () => run(() => api.post("/manager/referral-agreements", { ...f, rate_pct: parseFloat(f.rate_pct) }).then(() => setF({ enhancer_id: "", rate_pct: "", note: "" })), "Offer sent — it becomes active when the enhancer confirms.");
  return (
    <section className="eyw-card p-6 mt-8" data-testid="manager-referral-panel">
      <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Handshake size={18} className="text-[#D98E73]" /> Referral agreements <span className="text-xs text-[#8FA89B] font-sans">(optional)</span></h3>
      <p className="text-xs text-[#8FA89B] mb-4">Offer an approved enhancer a share of center-delivered sales they refer. Nothing changes until they confirm.</p>
      <div className="grid sm:grid-cols-[1fr_120px] gap-2">
        <select value={f.enhancer_id} onChange={(e) => setF({ ...f, enhancer_id: e.target.value })} className="eyw-input" data-testid="referral-offer-enhancer">
          <option value="">Choose an approved enhancer…</option>
          {approved.map((r) => <option key={r.enhancer_id} value={r.enhancer_id}>{r.enhancer_name || r.enhancer_email}</option>)}
        </select>
        <input type="number" min="0.5" max="90" step="0.5" value={f.rate_pct} onChange={(e) => setF({ ...f, rate_pct: e.target.value })} placeholder="% of sale" className="eyw-input" data-testid="referral-offer-rate" />
      </div>
      <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (optional)" className="eyw-input w-full mt-2" data-testid="referral-offer-note" />
      {parseFloat(f.rate_pct) > 0 && <p className="mt-2"><Split r={parseFloat(f.rate_pct)} /></p>}
      <button disabled={!f.enhancer_id || !(parseFloat(f.rate_pct) > 0)} onClick={offer} className="eyw-btn mt-3 !py-2 text-sm disabled:opacity-50" data-testid="referral-offer-btn">Offer rate</button>
      <ul className="divide-y divide-[#F1EDE6] mt-4">
        {(data?.agreements || []).map((a) => (
          <Row key={a.id} a={a} who={a.enhancer_name}>
            {["offered", "active"].includes(a.status) && <button onClick={() => run(() => api.post(`/manager/referral-agreements/${a.id}/end`), a.status === "offered" ? "Offer withdrawn." : "Agreement ended.")} className="eyw-btn-ghost !py-1.5 text-sm" data-testid={`referral-end-${a.id}`}>{a.status === "offered" ? "Withdraw" : "End"}</button>}
          </Row>
        ))}
      </ul>
      <CenterDeliveredToggles services={data?.center_services || []} run={run} />
    </section>
  );
};

export const EnhancerReferralOffers = () => {
  const run = useRun([["enh-referrals"]]);
  const { data = [] } = useQuery({ queryKey: ["enh-referrals"], queryFn: () => api.get("/enhancer/referral-agreements").then((r) => r.data) });
  const open = data.filter((a) => ["offered", "active"].includes(a.status));
  if (!open.length) return null;
  return (
    <section className="eyw-card p-6 mb-6" data-testid="enhancer-referral-offers">
      <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Handshake size={18} className="text-[#D98E73]" /> Center referral rates</h3>
      <p className="text-xs text-[#8FA89B] mb-3">When a client you refer books a service the center delivers itself, you earn this share of the sale. Services you perform yourself are paid as usual.</p>
      <ul className="divide-y divide-[#F1EDE6]">
        {open.map((a) => (
          <Row key={a.id} a={a} who={a.location_name}>
            {a.status === "offered" ? (
              <>
                <button onClick={() => run(() => api.post(`/enhancer/referral-agreements/${a.id}/accept`), "Confirmed — the rate is now active.")} className="eyw-btn !py-1.5 text-sm" data-testid={`referral-accept-${a.id}`}>Confirm {a.rate_pct}%</button>
                <button onClick={() => run(() => api.post(`/enhancer/referral-agreements/${a.id}/decline`), "Declined.")} className="eyw-btn-ghost !py-1.5 text-sm" data-testid={`referral-decline-${a.id}`}>Decline</button>
              </>
            ) : <button onClick={() => run(() => api.post(`/enhancer/referral-agreements/${a.id}/end`), "Agreement ended.")} className="eyw-btn-ghost !py-1.5 text-sm" data-testid={`referral-enh-end-${a.id}`}>End</button>}
          </Row>
        ))}
      </ul>
    </section>
  );
};

export const AdminReferralAgreements = () => {
  const run = useRun([["admin-referrals"]]);
  const [status, setStatus] = useState("active");
  const { data = [] } = useQuery({ queryKey: ["admin-referrals", status], queryFn: () => api.get("/admin/referral-agreements", { params: status ? { status } : {} }).then((r) => r.data) });
  return (
    <section className="eyw-card p-6 mt-8" data-testid="admin-referral-agreements">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Handshake size={18} className="text-[#D98E73]" /> Center referral agreements</h3>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="eyw-input !py-1.5 text-sm ml-auto w-44" data-testid="admin-referral-status">
          <option value="active">Active</option><option value="offered">Awaiting enhancer</option><option value="ended">Ended</option><option value="declined">Declined</option><option value="withdrawn">Withdrawn</option><option value="">All</option>
        </select>
      </div>
      {data.length === 0 ? <p className="text-sm text-[#8FA89B]" data-testid="admin-referral-empty">No agreements.</p> : (
        <ul className="divide-y divide-[#F1EDE6]">
          {data.map((a) => (
            <Row key={a.id} a={a} who={`${a.enhancer_name} ↔ ${a.location_name}`}>
              {["offered", "active"].includes(a.status) && <button onClick={() => run(() => api.post(`/admin/referral-agreements/${a.id}/end`), "Agreement ended.")} className="eyw-btn-ghost !py-1.5 text-sm" data-testid={`admin-referral-end-${a.id}`}>End</button>}
            </Row>
          ))}
        </ul>
      )}
      <AdminCenterDelivered />
    </section>
  );
};

const AdminCenterDelivered = () => {
  const run = useRun([["admin-center-services"]]);
  const { data = [] } = useQuery({ queryKey: ["admin-center-services"], queryFn: () => api.get("/admin/center-services").then((r) => r.data) });
  return (
    <div className="mt-6" data-testid="admin-center-delivered">
      <p className="eyw-label mb-2">Center-delivered services</p>
      <div className="grid sm:grid-cols-2 gap-1">
        {data.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-sm text-[#4A5D6B] px-2 py-1.5 rounded-lg hover:bg-[#144F75]/[0.04]" data-testid={`admin-center-delivered-${s.id}`}>
            <input type="checkbox" checked={s.center_delivered} className="accent-[#144F75]"
              onChange={(e) => run(() => api.patch(`/admin/services/${s.id}/center-delivered`, { center_delivered: e.target.checked }), "Saved.")} />
            {s.title} <span className="text-xs text-[#8FA89B]">· {s.center}</span>
          </label>
        ))}
      </div>
    </div>
  );
};
